"use client";

import Button from "@ishvaram/ui/Button";
import Input from "@ishvaram/ui/Input";
import { BirthPlaceField } from "./BirthPlaceField";
import TrustStrip from "./TrustStrip";
import { TIME_UNKNOWN_TOGGLE_CLASS } from "./BirthDetailsForm.styles";
import {
  fieldId,
  useBirthDetailsFormController,
  type BirthDetailsFormProps,
  type BirthFormLabels,
} from "./BirthDetailsForm.controller";

export default function BirthDetailsForm(props: BirthDetailsFormProps) {
  const form = useBirthDetailsFormController(props);
  const idScope = props.fieldIdScope ?? props.heading;

  return (
    // noValidate: the browser's bubble ("Please fill out this field.", in the
    // phone's UI language) blocked the submit before the form's own localized
    // copy could show (c-forms-06). Required fields keep `required` and
    // aria-required for their semantics; the controller validates them.
    <form
      translate="no"
      noValidate
      onSubmit={form.handleSubmit}
      className="card-sacred p-6 sm:p-8 space-y-5"
    >
      {form.showTrustStrip && <TrustStrip language={props.language ?? "en"} showCheckout={false} />}
      <FormHeading heading={props.heading} level={props.headingLevel} />
      {form.collectName && (
        <NameField
          idScope={idScope}
          labels={form.labels}
          name={form.name}
          setName={(value) => form.setField("name", value)}
          optionalName={form.optionalName}
          trackStart={form.trackStart}
          error={form.fieldErrors.name}
        />
      )}
      <DateTimeFields
        idScope={idScope}
        labels={form.labels}
        birthDate={form.birthDate}
        birthTime={form.birthTime}
        setBirthTime={(value) => form.setField("birthTime", value)}
        requireBirthTime={form.requireBirthTime}
        timeHint={form.timeHint}
        timeUnknown={{
          explainer: props.timeUnknownExplainer ?? form.labels.timeUnknownExplainer,
          open: form.timeUnknownOpen,
          onToggle: form.toggleTimeUnknown,
          locked: Boolean(props.collapseTimeUnknownOnSubmit && props.loading),
        }}
        todayIso={form.todayIso}
        dateError={form.dateError}
        timeError={form.fieldErrors.birthTime}
        handleDateChange={form.handleDateChange}
        trackStart={form.trackStart}
      />
      <BirthPlaceField
        id={fieldId("birthPlace", idScope)}
        labels={form.labels}
        birthPlace={form.birthPlace}
        selectedPlace={form.selectedPlace}
        setBirthPlace={(value) => form.setField("birthPlace", value)}
        setSelectedPlace={(value) => form.setField("selectedPlace", value)}
        placeIsUnresolved={form.placeIsUnresolved}
        trackStart={form.trackStart}
        error={form.fieldErrors.birthPlace}
        errorKey={form.blockedAttemptCount}
        noMatch={form.placeNoMatch}
        onNoMatchChange={form.setPlaceNoMatch}
      />
      {/* Keyed per blocked submit: a repeat tap re-mounts and re-announces it. */}
      <FormError key={form.blockedAttemptCount} error={props.error ?? form.submitBlockedError} />
      <SubmitButton
        loading={props.loading}
        calculatingLabel={form.labels.calculating}
        submitLabel={form.effectiveSubmitLabel}
      />
    </form>
  );
}

function FormHeading({ heading, level = "h3" }: { heading?: string; level?: "h2" | "h3" }) {
  if (!heading) return null;
  const Heading = level;
  return <Heading className="font-serif text-body font-bold text-text-primary">{heading}</Heading>;
}

/** The "Don't know your birth time?" disclosure; its open state lives in the controller. */
interface TimeUnknownHelp {
  explainer: string;
  open: boolean;
  onToggle: () => void;
  /** True while a collapseTimeUnknownOnSubmit form waits on its reply. */
  locked: boolean;
}

function NameField({
  idScope,
  labels,
  name,
  setName,
  optionalName,
  trackStart,
  error,
}: {
  idScope?: string;
  labels: BirthFormLabels;
  name: string;
  setName: (value: string) => void;
  optionalName: boolean;
  trackStart: () => void;
  error?: string;
}) {
  return (
    <Input
      id={fieldId("name", idScope)}
      type="text"
      required={!optionalName}
      aria-required={optionalName ? undefined : "true"}
      label={optionalName ? `${labels.yourName} ${labels.optional}` : labels.yourName}
      value={name}
      onChangeText={(value) => {
        trackStart();
        setName(value);
      }}
      placeholder={optionalName ? labels.namePlaceholderOptional : labels.enterFullName}
      autoComplete="name"
      autoCapitalize="words"
      error={error}
    />
  );
}

function DateTimeFields({
  idScope,
  labels,
  birthDate,
  birthTime,
  setBirthTime,
  requireBirthTime,
  timeHint,
  timeUnknown,
  todayIso,
  dateError,
  timeError,
  handleDateChange,
  trackStart,
}: {
  idScope?: string;
  labels: BirthFormLabels;
  birthDate: string;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  timeHint: string;
  timeUnknown: TimeUnknownHelp;
  todayIso: string;
  dateError?: string;
  timeError?: string;
  handleDateChange: (value: string) => void;
  trackStart: () => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Input
        id={fieldId("birthDate", idScope)}
        type="date"
        required
        aria-required="true"
        label={labels.birthDate}
        value={birthDate}
        onChangeText={handleDateChange}
        autoComplete="bday"
        min="1900-01-01"
        max={todayIso}
        error={dateError}
      />
      <BirthTimeField
        idScope={idScope}
        labels={labels}
        birthTime={birthTime}
        setBirthTime={setBirthTime}
        requireBirthTime={requireBirthTime}
        timeHint={timeHint}
        timeUnknown={timeUnknown}
        trackStart={trackStart}
        error={timeError}
      />
    </div>
  );
}

function BirthTimeField({
  idScope,
  labels,
  birthTime,
  setBirthTime,
  requireBirthTime,
  timeHint,
  timeUnknown,
  trackStart,
  error,
}: {
  idScope?: string;
  labels: BirthFormLabels;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  timeHint: string;
  timeUnknown: TimeUnknownHelp;
  trackStart: () => void;
  error?: string;
}) {
  return (
    <div>
      <label
        htmlFor={fieldId("birthTime", idScope)}
        className="mb-2 block text-body-small font-semibold text-text-primary"
      >
        {labels.birthTime}{" "}
        <span className="font-normal text-text-muted">
          {requireBirthTime ? labels.required : labels.optional}
        </span>
      </label>
      <Input
        id={fieldId("birthTime", idScope)}
        type="time"
        required={requireBirthTime}
        aria-required={requireBirthTime ? "true" : undefined}
        value={birthTime}
        onChangeText={(value) => {
          trackStart();
          setBirthTime(value);
        }}
        error={error}
      />
      <p className="mt-1.5 text-caption text-text-muted">{timeHint}</p>
      {!requireBirthTime && (
        <BirthTimeUnknownHelper toggleLabel={labels.timeUnknownToggle} help={timeUnknown} />
      )}
    </div>
  );
}

function BirthTimeUnknownHelper({
  toggleLabel,
  help,
}: {
  toggleLabel: string;
  help: TimeUnknownHelp;
}) {
  // Inline honesty disclosure (rule 32 dead-click + rule 22 voice): a timeless
  // entry never gets an exact Lagna, houses or dasha timing, and the Moon can
  // change rashi or nakshatra within the day (engine/time_sensitivity.py), so
  // the copy names that instead of promising reads it can't support. While locked
  // (collapseTimeUnknownOnSubmit, waiting on the reply) it shows as disabled.
  // The toggle's 44 px target and layout share: BirthDetailsForm.styles.ts.
  return (
    <div className="mt-1.5">
      <div className="flex">
        <button
          type="button"
          onClick={help.onToggle}
          aria-expanded={help.open}
          disabled={help.locked}
          className={TIME_UNKNOWN_TOGGLE_CLASS}
        >
          {toggleLabel}
        </button>
      </div>
      {help.open && <p className="mt-1.5 text-caption text-text-muted">{help.explainer}</p>}
    </div>
  );
}

function FormError({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      className="text-body-small rounded-lg p-3.5"
      style={{
        color: "var(--color-error-text)",
        backgroundColor: "var(--color-error-bg)",
        border: "1px solid var(--color-error-tint)",
      }}
    >
      {error}
    </div>
  );
}

function SubmitButton({
  loading,
  calculatingLabel,
  submitLabel,
}: {
  loading: boolean;
  calculatingLabel: string;
  submitLabel: string;
}) {
  return (
    <Button
      type="submit"
      onPress={() => {
        /* form.onSubmit handles it — this keeps Enter-key behavior */
      }}
      disabled={loading}
      loading={loading}
      variant="primary"
      title={loading ? calculatingLabel : `${submitLabel} \u2192`}
      className="w-full py-3.5"
    />
  );
}
